// ============================================================
// Definición versionada del instrumento de Riesgo Psicosocial
// (Ministerio del Trabajo del Ecuador)
//
// CITA REAL (fuente): Ministerio del Trabajo del Ecuador, Guía para la
// aplicación del cuestionario de evaluación de riesgo psicosocial (octubre
// 2018). De ese documento salen, parafraseados (sin citar pasajes
// textuales largos): la escala de respuesta de 4 puntos (Tabla 1), la
// agrupación de los 58 ítems en 8 dimensiones (Tabla 2), los rangos de
// clasificación bajo/medio/alto por dimensión y por subdimensión de
// "Otros puntos importantes" (Tabla 3), y los rangos de clasificación
// globales (Tabla 4).
//
// DECISIÓN PROPIA (implementación, no de la fuente):
//   - Los `key` de cada dimensión/subdimensión, los nombres de los tipos
//     (RiskLevel, RiskRange, DimensionDef, etc.) y la forma en que se
//     estructuran los datos (arrays de objetos tipados) son decisiones de
//     este código, no de la guía.
//   - El puntaje se calcula como suma simple de las respuestas de cada
//     ítem, sin invertir ningún ítem. La guía indica una suma simple, y el
//     texto de los 58 ítems (que se agrega en un paso posterior) está
//     redactado en sentido favorable — pero esto NO se ha verificado ítem
//     por ítem contra el cuestionario completo, así que queda pendiente
//     confirmar con Kevin al construir el formulario que ningún ítem
//     requiere inversión de puntaje.
//
// El texto literal de los 58 ítems NO vive en este archivo — se agrega en
// el paso de construcción de la página del formulario.
//
// Solo tipos y constantes de datos. Sin funciones de cálculo — el motor
// de puntuación (suma, clasificación) se construye en un paso posterior.
// ============================================================

export type RiskLevel = 'bajo' | 'medio' | 'alto';

export interface RiskRange {
  min: number;
  max: number;
}

export interface RiskCutoffs {
  bajo: RiskRange;
  medio: RiskRange;
  alto: RiskRange;
}

export interface ResponseOption {
  value: 1 | 2 | 3 | 4;
  label: string;
}

export interface SubdimensionDef {
  key: string;
  label: string;
  items: number[];
  cutoffs: RiskCutoffs;
}

export interface DimensionDef {
  key: string;
  label: string;
  items: number[];
  cutoffs: RiskCutoffs;
  subdimensions?: SubdimensionDef[];
}

export interface InstrumentDef {
  id: string;
  version: string;
  source: string;
  itemCount: number;
  responseScale: ResponseOption[];
  dimensions: DimensionDef[];
  globalCutoffs: RiskCutoffs;
}

// Escala de respuesta de 4 puntos — Guía MDT, Tabla 1.
export const PSICOSOCIAL_RESPONSE_SCALE: ResponseOption[] = [
  { value: 4, label: 'Completamente de acuerdo' },
  { value: 3, label: 'Parcialmente de acuerdo' },
  { value: 2, label: 'Poco de acuerdo' },
  { value: 1, label: 'En desacuerdo' },
];

// Subdimensiones de "Otros puntos importantes" — Guía MDT, Tabla 2
// (agrupación de ítems) y Tabla 3 (rangos bajo/medio/alto).
const OTROS_PUNTOS_IMPORTANTES_SUBDIMENSIONES: SubdimensionDef[] = [
  {
    key: 'acoso_discriminatorio',
    label: 'Acoso discriminatorio',
    items: [35, 38, 53, 56],
    cutoffs: { bajo: { min: 13, max: 16 }, medio: { min: 8, max: 12 }, alto: { min: 4, max: 7 } },
  },
  {
    key: 'acoso_laboral',
    label: 'Acoso laboral',
    items: [41, 50],
    cutoffs: { bajo: { min: 7, max: 8 }, medio: { min: 5, max: 6 }, alto: { min: 2, max: 4 } },
  },
  {
    key: 'acoso_sexual',
    label: 'Acoso sexual',
    items: [43, 48],
    cutoffs: { bajo: { min: 7, max: 8 }, medio: { min: 5, max: 6 }, alto: { min: 2, max: 4 } },
  },
  {
    key: 'adiccion_trabajo',
    label: 'Adicción al trabajo',
    items: [36, 45, 51, 55, 57],
    cutoffs: { bajo: { min: 16, max: 20 }, medio: { min: 10, max: 15 }, alto: { min: 5, max: 9 } },
  },
  {
    key: 'condiciones_trabajo',
    label: 'Condiciones del trabajo',
    items: [40, 47],
    cutoffs: { bajo: { min: 7, max: 8 }, medio: { min: 5, max: 6 }, alto: { min: 2, max: 4 } },
  },
  {
    key: 'doble_presencia',
    label: 'Doble presencia (laboral – familiar)',
    items: [46, 49],
    cutoffs: { bajo: { min: 7, max: 8 }, medio: { min: 5, max: 6 }, alto: { min: 2, max: 4 } },
  },
  {
    key: 'estabilidad_laboral_emocional',
    label: 'Estabilidad laboral y emocional',
    items: [37, 39, 42, 52, 54],
    cutoffs: { bajo: { min: 16, max: 20 }, medio: { min: 10, max: 15 }, alto: { min: 5, max: 9 } },
  },
  {
    key: 'salud_auto_percibida',
    label: 'Salud auto percibida',
    items: [44, 58],
    cutoffs: { bajo: { min: 7, max: 8 }, medio: { min: 5, max: 6 }, alto: { min: 2, max: 4 } },
  },
];

// Las 8 dimensiones del cuestionario, con sus ítems y rangos de
// clasificación — Guía MDT, Tabla 2 (agrupación de ítems) y Tabla 3
// (rangos bajo/medio/alto por dimensión y por subdimensión). "Otros
// puntos importantes" es la única dimensión con subdimensiones.
export const PSICOSOCIAL_DIMENSIONS: DimensionDef[] = [
  {
    key: 'carga_ritmo',
    label: 'Carga y ritmo de trabajo',
    items: [1, 2, 3, 4],
    cutoffs: { bajo: { min: 13, max: 16 }, medio: { min: 8, max: 12 }, alto: { min: 4, max: 7 } },
  },
  {
    key: 'desarrollo_competencias',
    label: 'Desarrollo de competencias',
    items: [5, 6, 7, 8],
    cutoffs: { bajo: { min: 13, max: 16 }, medio: { min: 8, max: 12 }, alto: { min: 4, max: 7 } },
  },
  {
    key: 'liderazgo',
    label: 'Liderazgo',
    items: [9, 10, 11, 12, 13, 14],
    cutoffs: { bajo: { min: 18, max: 24 }, medio: { min: 12, max: 17 }, alto: { min: 6, max: 11 } },
  },
  {
    key: 'margen_accion_control',
    label: 'Margen de acción y control',
    items: [15, 16, 17, 18],
    cutoffs: { bajo: { min: 13, max: 16 }, medio: { min: 8, max: 12 }, alto: { min: 4, max: 7 } },
  },
  {
    key: 'organizacion_trabajo',
    label: 'Organización del trabajo',
    items: [19, 20, 21, 22, 23, 24],
    cutoffs: { bajo: { min: 18, max: 24 }, medio: { min: 12, max: 17 }, alto: { min: 6, max: 11 } },
  },
  {
    key: 'recuperacion',
    label: 'Recuperación',
    items: [25, 26, 27, 28, 29],
    cutoffs: { bajo: { min: 16, max: 20 }, medio: { min: 10, max: 15 }, alto: { min: 5, max: 9 } },
  },
  {
    key: 'soporte_apoyo',
    label: 'Soporte y apoyo',
    items: [30, 31, 32, 33, 34],
    cutoffs: { bajo: { min: 16, max: 20 }, medio: { min: 10, max: 15 }, alto: { min: 5, max: 9 } },
  },
  {
    key: 'otros_puntos_importantes',
    label: 'Otros puntos importantes',
    items: [
      35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46,
      47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58,
    ],
    cutoffs: { bajo: { min: 73, max: 96 }, medio: { min: 49, max: 72 }, alto: { min: 24, max: 48 } },
    subdimensions: OTROS_PUNTOS_IMPORTANTES_SUBDIMENSIONES,
  },
];

// Rangos de clasificación global (suma de las 8 dimensiones, 58 a 232
// puntos) — Guía MDT, Tabla 4.
export const PSICOSOCIAL_GLOBAL_CUTOFFS: RiskCutoffs = {
  bajo: { min: 175, max: 232 },
  medio: { min: 117, max: 174 },
  alto: { min: 58, max: 116 },
};

export const PSICOSOCIAL_INSTRUMENT: InstrumentDef = {
  id: 'mdt-ec-riesgo-psicosocial',
  version: '2018-10',
  source: 'Ministerio del Trabajo del Ecuador, Guía para la aplicación del cuestionario de evaluación de riesgo psicosocial (octubre 2018), Tablas 1 a 4 y Anexo 11.1',
  itemCount: 58,
  responseScale: PSICOSOCIAL_RESPONSE_SCALE,
  dimensions: PSICOSOCIAL_DIMENSIONS,
  globalCutoffs: PSICOSOCIAL_GLOBAL_CUTOFFS,
};
